import { Card } from "@components/cards/card";
import { api } from "@service/api";
import Skeleton from "react-loading-skeleton";
import { useEffect, useState } from "react";
import {
  IRaffleMyOwnListDTO,
  RaffleStatusTypes,
} from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { ProgressBar } from "react-bootstrap";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { FaMoneyBill1Wave, FaMoneyBillTrendUp } from "react-icons/fa6";
import { Masks } from "@hooks/functions/Masks/Masks";
import { FaCheckCircle, FaShoppingBag } from "react-icons/fa";
import {
  getRaffleProfitType,
  getRaffleType,
} from "@hooks/functions/Raffle/type";
import dayjs from "dayjs";
import { clickToCopy } from "@hooks/functions/Misc/ClickToCopy";
import { ToastContainer } from "react-toastify";
import { Pagination } from "@components/pagination";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  IRaffleDefaultProfitTypeDTO,
  IRaffleDefaultTypeDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { Link } from "react-router-dom";

export function RaffleMyOwnList() {
  // Plugins & Functions
  const masks = new Masks();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // Control Things & General Configs
  const [isLoading, setIsLoading] = useState<{ raffles: boolean }>({
    raffles: true,
  });
  const [raffles, setRaffles] = useState<IRaffleMyOwnListDTO[]>([]);

  // Pagination Part
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("DESC");
  const [quantity, setQuantity] = useState(8);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [status, setStatus] = useState<RaffleStatusTypes | "">("");
  const [type, setType] = useState<IRaffleDefaultTypeDTO | "">("");
  const [profitType, setProfitType] = useState<
    IRaffleDefaultProfitTypeDTO | ""
  >("");

  async function loadCustomerOwnRaffles() {
    try {
      const params = `?order=${order}&page=${page}&quantity=${quantity}&status=${status}&type=${type}&profitType=${profitType}`;
      const result = await api.get(`/customer/raffle/default/my-list${params}`);

      setRaffles(result.data.result);

      setTotalPages(Math.ceil(Number(result.data.total / quantity)) || 1);

      setIsLoading((prev) => ({
        ...prev,
        raffles: false,
      }));
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "",
        type: "error",
        theme: "dark",
      });
    }
  }

  useEffect(() => {
    setIsLoading((prev) => ({
      ...prev,
      raffles: true,
    }));

    loadCustomerOwnRaffles();
  }, [page, order, quantity, status]);

  return (
    <>
      <Card className="rafflesMyOwnList">
        <div className="card-header">
          <div className="row d-flex flex justify-content-between">
            <div className="col-md-8">
              <div className="card-title">Minha Rifas</div>
              <div className="card-category">
                Listagem de todas as minhas rifas
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex flex justify-content-end align-items-center">
                <div className="me-4">Filtros</div>
                {/* Status */}
                <div className="form-group">
                  <select
                    className="form-select"
                    aria-label="Filtro por Status"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value as RaffleStatusTypes | "");
                    }}
                  >
                    <option value="">Status</option>
                    <option value="a">Ativos</option>
                    <option value="i">Inativos</option>
                    <option value="p">Pendentes</option>
                    <option value="f">Finalizados</option>
                    <option value="x">Excluídos</option>
                  </select>
                </div>
                {/* Ordenation */}
                <div className="form-group">
                  <select
                    className="form-select"
                    aria-label="Ordenação das rifas"
                    value={order}
                    onChange={(e) => {
                      setOrder(e.target.value);
                    }}
                  >
                    <option value="DESC">Recentes</option>
                    <option value="ASC">Antigas</option>
                  </select>
                </div>
                {/* New Raffle */}
                <div className="">
                  <Link
                    className="btn btn-sm btn-success"
                    to={`/raffle/default/new`}
                  >
                    Criar Campanha
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {isLoading.raffles ? (
            <Skeleton count={4} height={25} borderRadius={5} />
          ) : (
            <>
              <div className="row d-flex flex">
                {raffles.length === 0 ? (
                  <div className="d-flex justify-content-center">
                    <span className="h5">Nenhum registro encontrado.</span>
                  </div>
                ) : (
                  <>
                    {raffles.map((raffle, index) => {
                      const raffleStatus = getRaffleStatus(
                        raffle?.status as RaffleStatusTypes,
                      );
                      const raffleType = getRaffleType(raffle?.type);
                      const raffleProfitType = getRaffleProfitType(
                        raffle?.profitType,
                      );

                      return (
                        <div
                          key={index}
                          className="col-12 col-md-6 col-lg-4 col-xl-3 mb-4 d-flex align-items-stretch"
                        >
                          <Card className="w-100 h-100">
                            {/* Card Header */}
                            <div className="card-header pb-2">
                              {/* Status & Type */}
                              <div className="row mb-2 g-0 text-center text-md-start">
                                <div
                                  className="col-6"
                                  title={`${raffleStatus?.title}`}
                                >
                                  <span
                                    className={`badge badge-${raffleStatus?.color} fs-6 w-100`}
                                  >
                                    {raffleStatus?.text}
                                  </span>
                                </div>
                                <div
                                  className="col-6"
                                  title={`${raffleType?.title}`}
                                >
                                  <span className="badge badge-secondary fs-6 w-100">
                                    {raffleType?.text}
                                  </span>
                                </div>
                              </div>
                              {/* Header */}
                              <Link
                                to={`/raffle/default/view?campaign=${
                                  raffle?.campaign
                                }${raffle?.key ? `&key=${raffle?.key}` : ``}`}
                              >
                                <div className="d-flex flex-column text-center">
                                  <div className="card-title">
                                    {raffle?.name}
                                  </div>
                                </div>
                              </Link>
                            </div>

                            {/* Card Body */}
                            <div className="card-body d-flex flex-column pb-2">
                              {/* Campaign Cover */}
                              <Link
                                to={`/raffle/default/view?campaign=${
                                  raffle?.campaign
                                }${raffle?.key ? `&key=${raffle?.key}` : ``}`}
                              >
                                <div
                                  className="container-fluid d-flex flex-column justify-content-center"
                                  style={{ minHeight: "250px" }}
                                >
                                  <img
                                    src={raffle?.thumb}
                                    alt="Campaign Cover"
                                    className="img-fluid"
                                    style={{
                                      maxHeight: "250px",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              </Link>

                              {/* Description Short */}
                              <div className="row mt-2">
                                <div className="text-center">
                                  <span>{raffle?.description_short}</span>
                                </div>
                              </div>

                              <hr />

                              <div className="d-flex flex-column flex-grow-1 justify-content-center gap-3">
                                {/* Progress Bar */}
                                <div className="row">
                                  <div className="col-11 mx-auto text-center">
                                    <span className="fw-bolder">
                                      {raffle?.ticketSoldProgress}% dos tickets
                                      já foram vendidos.
                                    </span>
                                    <ProgressBar
                                      now={raffle?.ticketSoldProgress}
                                      label={`${raffle?.ticketSoldProgress}%`}
                                      variant="primary"
                                      animated
                                    />
                                  </div>
                                </div>

                                {/* Amounts (Total and Sold) */}
                                <div className="row ms-4">
                                  <div
                                    className="col-6 d-flex align-items-center"
                                    title={`Quantidade total de Tickets.`}
                                  >
                                    <FaShoppingBag className="fs-5 me-2" />
                                    <span>
                                      {masks.formatMilhar(
                                        Number(raffle?.quantity),
                                      )}
                                    </span>
                                  </div>
                                  <div
                                    className="col-6 d-flex align-items-center"
                                    title={`Quantidade total de Tickets vendidos.`}
                                  >
                                    <FaCheckCircle className="fs-5 me-2" />
                                    <span>
                                      {masks.formatMilhar(
                                        Number(raffle?.ticketSoldAmount),
                                      )}
                                    </span>
                                  </div>
                                </div>

                                {/* Price and Profit */}
                                <div className="row ms-4">
                                  <div
                                    className="col-6 d-flex align-items-center"
                                    title={`Preço unitário padrão do Ticket.`}
                                  >
                                    <FaMoneyBill1Wave className="fs-5 me-2" />
                                    <span>
                                      {masks.brlCurrency(raffle?.price)}
                                    </span>
                                  </div>
                                  <div
                                    className="col-6 d-flex align-items-center"
                                    title={`Lucro líquido da venda de Tickets até o momento (EM BREVE).`}
                                  >
                                    <FaMoneyBillTrendUp className="fs-5 me-2" />
                                    <span>
                                      {masks.brlCurrency(raffle?.profitTotal)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Campaign Deep Info */}
                              <div className="d-flex flex-column justify-content-between">
                                <div className="row">
                                  {/* Campaign Code */}
                                  <div
                                    className="col-md-6 mb-2"
                                    title={`Código do link da Campanha (Para compartilhamento)`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      clickToCopy({
                                        text: `${window.location.origin}/campaign=${raffle?.campaign}`,
                                        onSuccess: () =>
                                          pushToastify({
                                            message: "Copiado com sucesso!",
                                            type: "success",
                                            theme: "dark",
                                          }),
                                        onError: () =>
                                          pushToastify({
                                            message: "Falha ao copiar.",
                                            type: "error",
                                            theme: "dark",
                                          }),
                                      });
                                    }}
                                  >
                                    <span
                                      className={`badge bg-light text-dark fs-6 w-100`}
                                    >
                                      #{raffle?.campaign}
                                    </span>
                                  </div>

                                  {/* Campaign Unknown */}
                                  <div
                                    className="col-md-6"
                                    title={raffleProfitType?.text}
                                  >
                                    <span
                                      className={`badge bg-light text-dark fs-6 w-100`}
                                    >
                                      {raffleProfitType?.title}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card Footer */}
                            <div className="card-footer text-end">
                              {/* Dates */}
                              <div className="row d-flex flex-column">
                                <span>
                                  Criada em:{" "}
                                  {dayjs(raffle?.createdAt).format(
                                    "DD/MM/YYYY [às] HH:mm:ss",
                                  )}
                                </span>
                                {raffle?.finishedAt ? (
                                  <span>
                                    Finalizada em:{" "}
                                    {dayjs(raffle?.finishedAt).format(
                                      "DD/MM/YYYY [às] HH:mm:ss",
                                    )}
                                  </span>
                                ) : (
                                  <span>Em andamento</span>
                                )}
                              </div>
                            </div>
                          </Card>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <div className="">
              <span>
                Página {page} de {totalPages} página
                {totalPages > 1 ? `s` : ``}.
              </span>
            </div>
            <div className="">
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={5}
              />
            </div>
          </div>
        </div>
      </Card>
      <ToastContainer />
    </>
  );
}
